import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import { TimesheetCalendarSourceType } from 'api/timesheets';
import {
  AuthMeta,
  CalendarIntegrationProviderSetupApi,
  CalendarIntegrations,
  TimesheetConfiguration,
} from 'api/timesheets/integration/calendar-integration-provider';
import { TIMESHEET_CONFIGURATION_KEY } from './configuration';

const getOauthQueryKey = () => 'timesheet-calendar-integration-provider-meta';
const getIntegrationQueryKey = () => 'timesheet-calender-integration-provider';
const getAccountTimesheetIntegrationProvider = () =>
  `${getIntegrationQueryKey()}account`;

export const useTimesheetIntegrationOauthQuery = (
  sourceType: TimesheetCalendarSourceType,
): UseQueryResult<AuthMeta> => {
  return useQuery(
    getOauthQueryKey(),
    () =>
      sourceType === TimesheetCalendarSourceType.GoogleCalendar
        ? CalendarIntegrationProviderSetupApi.getGoogleOauth()
        : CalendarIntegrationProviderSetupApi.getOutlookOauth(),
    {
      refetchOnMount: true,
    },
  );
};

export const useTimesheetIntegrationsQuery = (
  sourceType: TimesheetCalendarSourceType,
): UseQueryResult<CalendarIntegrations> => {
  return useQuery(
    getIntegrationQueryKey(),
    () =>
      sourceType === TimesheetCalendarSourceType.GoogleCalendar
        ? CalendarIntegrationProviderSetupApi.getGoogleIntegrations()
        : CalendarIntegrationProviderSetupApi.getOutlookIntegrations(),
    {
      refetchOnMount: true,
    },
  );
};

export const useTimesheetLogoutMutation = (): UseMutationResult<
  unknown,
  unknown,
  { integrationId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ integrationId }) =>
      CalendarIntegrationProviderSetupApi.deleteIntegration(integrationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getIntegrationQueryKey());
      },
    },
  );
};

export const useAccountTimesheetIntegrationProvidersQuery =
  (): UseQueryResult<TimesheetConfiguration> => {
    return useQuery(
      getAccountTimesheetIntegrationProvider(),
      () => CalendarIntegrationProviderSetupApi.getTimesheetConfiguration(),
      {
        refetchOnMount: true,
      },
    );
  };

export const useAccountTimesheetIntegrationProvidersMutation =
  (): UseMutationResult<
    unknown,
    unknown,
    { payload: TimesheetConfiguration }
  > => {
    const queryClient = useQueryClient();
    const queryKey = getAccountTimesheetIntegrationProvider();

    return useMutation(
      ({ payload }) =>
        CalendarIntegrationProviderSetupApi.updateTimesheetConfiguration(
          payload,
        ),
      {
        onSuccess: async (config) => {
          await queryClient.invalidateQueries(queryKey, { exact: false });
          await queryClient.invalidateQueries(getOauthQueryKey(), {
            exact: false,
          });
          await queryClient.invalidateQueries(getIntegrationQueryKey(), {
            exact: false,
          });
          await queryClient.invalidateQueries(
            getAccountTimesheetIntegrationProvider(),
            { exact: false },
          );
          await queryClient.setQueryData(TIMESHEET_CONFIGURATION_KEY, config);
        },
      },
    );
  };
